<script setup lang="ts">
/**
 * Подтверждение подъёма и гашения фича-стенда.
 *
 * Спрашиваем не «уверены ли вы», а «с какой ветки»: у задачи их обычно
 * несколько — фронт и бэкенд, — и поднимать надо ту, что нужна сейчас.
 * Лимит окружений общий на всех, поэтому он тут же и написан.
 */
import { AppButton, AppModal } from '@vinprop/design-system'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import type { DevBranch, DevStand } from '@/features/issue/devinfo'

const props = defineProps<{
  branches: DevBranch[]
  stands: DevStand[]
  /** Гасим существующий стенд, а не поднимаем новый */
  drop?: boolean
  busy?: boolean
}>()

const emit = defineEmits<{
  submit: [projectPath: string, branch: string]
  close: []
}>()

const { t } = useI18n()

const picked = ref(0)

/**
 * Гасить можно только то, что поднято; поднимать — с любой ветки задачи.
 * Стенд знает свой адрес, но не ветку: адрес собран из ключа задачи, поэтому
 * ветку берём из той же задачи по имени репозитория.
 */
const options = computed(() =>
  props.drop
    ? props.stands
        .filter((stand) => !stand.isRemoved)
        .map((stand) => ({
          label: stand.host,
          projectPath: stand.host.split('-')[0] ?? '',
          branch: branchFor(stand.host),
        }))
    : props.branches.map((branch) => ({
        label: `${branch.projectPath}: ${branch.name}`,
        projectPath: branch.projectPath,
        branch: branch.name,
      })),
)

/** Ветка, с которой поднят стенд: ищем по имени репозитория в адресе */
function branchFor(host: string): string {
  const found = props.branches.find((branch) => host.startsWith(branch.projectPath.split('/').pop() ?? ''))

  return found?.name ?? ''
}

const submit = () => {
  const option = options.value[picked.value]

  if (option) emit('submit', option.projectPath, option.branch)
}
</script>

<template>
  <AppModal
    model-value
    :title="drop ? t('devops.stand.dropTitle') : t('devops.stand.raiseTitle')"
    size="small"
    @update:model-value="emit('close')"
  >
    <div class="stand">
      <p class="stand__hint">
        {{ drop ? t('devops.stand.dropHint') : t('devops.stand.raiseHint') }}
      </p>

      <p v-if="!options.length" class="stand__empty">{{ t('devops.stand.nothing') }}</p>

      <label
        v-for="(option, index) in options"
        :key="option.label"
        class="stand__option"
        :class="{ 'stand__option--picked': picked === index }"
      >
        <input v-model="picked" type="radio" :value="index" class="stand__radio" />
        <span class="stand__label">{{ option.label }}</span>
      </label>
    </div>

    <template #footer>
      <AppButton variant="secondary" @click="emit('close')">
        {{ t('common.actions.cancel') }}
      </AppButton>
      <AppButton
        :variant="drop ? 'danger' : 'primary'"
        :loading="busy"
        :disabled="!options.length"
        @click="submit"
      >
        {{ drop ? t('devops.stand.drop') : t('devops.stand.raise') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.stand {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stand__hint,
.stand__empty {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.stand__option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
}

.stand__option--picked {
  border-color: var(--color-primary);
  background: var(--color-primary-tint);
}

.stand__label {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
}
</style>
